import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { errMsg } from "../lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { LowCreditAlert } from "./LowCreditAlert";

export function useAiImages({ api, pid, onDone, enabled = true }) {
  const [st, setSt] = useState(null);
  const timer = useRef(null);
  const load = useCallback(() => api.get(`/projects/${pid}/images`).then((r) => {
    setSt((prev) => {
      if (prev?.job?.status === "running" && r.data.job?.status !== "running") {
        const ok = (r.data.job?.results || []).filter((x) => x.asset_id).length;
        if (r.data.job?.status === "done") toast.success(`${ok} of ${r.data.job.total} AI image(s) added to your website.`); else toast.error("Image creation stopped. Nothing more will be tried automatically.");
        window.dispatchEvent(new Event("altiora:credits"));
        onDone?.();
      }
      return r.data;
    });
  }).catch(() => setSt(null)), [api, pid, onDone]);
  useEffect(() => { if (enabled) load(); }, [load, enabled]);
  useEffect(() => {
    clearTimeout(timer.current);
    if (st?.job?.status === "running") timer.current = setTimeout(load, 3000);
    return () => clearTimeout(timer.current);
  }, [st, load]);
  const start = async (url, body) => {
    try { await api.post(url, { confirm: true, ...body }); await load(); window.dispatchEvent(new Event("altiora:credits")); } catch (e) { toast.error(errMsg(e)); }
  };
  const cr = st?.credits;
  const credit = (n) => (cr?.required ? ` This uses ${n * cr.per_image} AI image credit${n * cr.per_image === 1 ? "" : "s"} (your balance: ${cr.balance}). Failed images are refunded automatically.` : "");
  return { st, running: st?.job?.status === "running", regenLeft: st ? st.regen_max - st.regen_used : 0, credit,
    generate: () => start(`/projects/${pid}/images/generate`, {}), regen: (loc) => start(`/projects/${pid}/images/regenerate`, loc) };
}

export function Confirm({ open, setOpen, title, text, onOk }) {
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent data-testid="ai-image-confirm">
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{text}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel data-testid="ai-image-confirm-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={onOk} data-testid="ai-image-confirm-ok">Yes, create</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>);
}

export function ImagesBar({ ai, api }) {
  const [open, setOpen] = useState(false);
  const [bgAsk, setBgAsk] = useState(false);
  const { st, running } = ai;
  if (!st) return null;
  const left = Math.max(0, st.initial_max - st.initial_used);
  const regenLeft = st.regen_max - st.regen_used;
  const bgLeft = Math.max(0, (st.bg_max ?? 3) - (st.bg_used || 0));
  const cr = st.credits;
  const short = cr?.required && cr.balance < cr.per_image;
  return (
    <div className="space-y-2">
    {api && <LowCreditAlert w={st.credits} api={api} />}
    <div className="card-lux !p-3 flex flex-wrap items-center gap-3 text-sm" data-testid="ai-images-bar">
      <Sparkles className="w-4 h-4 text-[#b8892b]" />
      {st.credits?.required && <a href="/dashboard/credits" target="_blank" rel="noreferrer" className="chip" data-testid="ai-credit-balance">AI image credits: <b className="ml-1">{st.credits.balance}</b></a>}
      {running ? <span className="inline-flex items-center gap-2" data-testid="ai-images-progress"><Loader2 className="w-4 h-4 animate-spin" />Creating image {Math.min(st.job.done + 1, st.job.total)} of {st.job.total}… you can keep editing.</span>
        : st.initial_done ? <span data-testid="ai-images-status">AI images created. Regenerations left: <b>{st.regen_max - st.regen_used}</b> of {st.regen_max} — use the image fields in the editor, or upload your own.</span>
          : <>
            <span data-testid="ai-images-status">Add up to {left} photos made for your business (based on "How we understood your business").</span>
            <button onClick={() => setOpen(true)} disabled={!left} className="btn-gold !py-2" data-testid="ai-images-generate">Create my AI images</button>
          </>}
      {!running && (
        <div className="basis-full flex flex-wrap items-center gap-2 border-t pt-2" data-testid="background-chooser">
          <span className="text-xs font-semibold text-[#062a20]">Background:</span>
          <span className="text-xs text-slate-600">Free code-drawn styles — use "Change background" above (no credits)</span>
          <span className="text-xs text-slate-400">or</span>
          <button onClick={() => setBgAsk(true)} disabled={bgLeft < 1 || short} className="chip disabled:opacity-40" data-testid="ai-background-generate">AI background{cr?.required ? ` · ${cr.per_image} credit` : ""}</button>
          {short && <a href="/dashboard/credits" target="_blank" rel="noreferrer" className="text-xs underline" data-testid="ai-background-buy">Buy credits</a>}
          {bgLeft < 1 && <span className="text-xs text-slate-500">AI background limit reached for this project.</span>}
        </div>)}
      <Confirm open={bgAsk} setOpen={setBgAsk} title="Create an AI background?" onOk={() => ai.regen({ kind: "background" })}
        text={`We'll create one subtle background made for your brand colours and show it behind your whole website (preview and final site).${cr?.required ? ` Cost: ${cr.per_image} AI image credit (balance: ${cr.balance}). Credits are only deducted if the background is created successfully.` : ""} ${bgLeft} AI background${bgLeft === 1 ? "" : "s"} left for this project. "Change background" switches back to the free styles at any time.`} />
      <Confirm open={open} setOpen={setOpen} title={`Create ${left} AI images?`} onOk={ai.generate}
        text={`We'll create up to ${left} images for your website in one go. This can only be done once; after that you can regenerate single images (${st.regen_max} in total) or upload your own. Spots that already have your photos are kept.${ai.credit(left)}`} />
    </div>
    </div>);
}
